import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getTodaysChallenges, revealStretchChallenge } from '../../api/challenges';
import starImg from '../../assets/stamps/star.webp';
import pandaImg from '../../assets/stamps/panda.webp';
import axolotlImg from '../../assets/stamps/axolotl.webp';
import dragonImg from '../../assets/stamps/dragon.webp';
import shipImg from '../../assets/stamps/ship.webp';
import cheetahImg from '../../assets/stamps/cheetah.webp';
import owlImg from '../../assets/stamps/owl.webp';
import catImg from '../../assets/stamps/cat.webp';
import s from './ChallengeDrawer.module.css';

const DESKTOP_BREAKPOINT = 1280;
const SPRING = { type: 'spring', stiffness: 380, damping: 36 };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

const STAMPS = [
  { src: pandaImg, alt: 'Red Panda stamp' },
  { src: axolotlImg, alt: 'Axolotl stamp' },
  { src: dragonImg, alt: 'Dragon stamp' },
  { src: shipImg, alt: 'Story Ship stamp' },
  { src: cheetahImg, alt: 'Cheetah stamp' },
  { src: owlImg, alt: 'Owl stamp' },
  { src: catImg, alt: 'Cat stamp' },
];
const STAMP_TOTAL = STAMPS.length;

const stampsContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};
const stampVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: 'spring', stiffness: 420, damping: 18 },
  },
};

function SweepRow({ progress, target, done, t }) {
  const pct = Math.min(100, Math.round((progress / Math.max(target, 1)) * 100));
  return (
    <>
      <div className={s.sweepHeader}>
        <span className={s.sweepLabel}>{t('map.weeklySweep')}</span>
        <span className={s.sweepProgress}>{done ? t('map.done') : `${progress}/${target}`}</span>
      </div>
      <div className={s.progressBar}>
        <div
          className={`${s.progressFill} ${done ? s.progressFillDone : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </>
  );
}

function TodayRow({ label, progress, target, done, xpHint, bonus, t }) {
  return (
    <div className={`${s.todayRowWrap} ${bonus ? s.todayRowWrapBonus : ''}`}>
      {bonus && <span className={s.bonusPill}>{t('map.bonus')}</span>}
      <div className={s.todayRow}>
        <span className={s.todayLabel}>{label}</span>
        {done ? (
          <span className={s.doneTag}>{t('map.done')}</span>
        ) : (
          <span className={s.xpReward}>
            {xpHint ?? `${progress}/${target}`}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ChallengeDrawer({ childId, open, onClose }) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['challenges', childId],
    queryFn: () => getTodaysChallenges(childId),
    enabled: !!childId && open,
    staleTime: 30_000,
  });

  const reveal = useMutation({
    mutationFn: () => revealStretchChallenge(childId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges', childId] }),
  });

  const stampTotal = data?.stamps?.total ?? 0;
  const breakthroughReady = !!data?.stamps?.breakthrough_ready;

  const drawerMotionProps = isDesktop
    ? { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        drag: 'y',
        dragConstraints: { top: 0, bottom: 0 },
        dragElastic: { top: 0, bottom: 0.4 },
        onDragEnd: (_, info) => {
          if (info.offset.y > 100 || info.velocity.y > 500) onClose();
        },
      };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={s.scrim}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={`${s.drawer} ${isDesktop ? s.drawerDesktop : ''}`}
            role="dialog"
            aria-label="Today's challenges"
            transition={SPRING}
            {...drawerMotionProps}
          >
            <div className={s.grabber} aria-hidden="true" />

            <button
              type="button"
              className={s.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>

            <header className={s.header}>
              <img src={starImg} alt="" className={s.starIcon} aria-hidden="true" />
              <h2 className={s.title}>{t('map.challengesTitle')}</h2>
            </header>

            {breakthroughReady && (
              <p className={s.breakthroughBanner}>
                <span aria-hidden="true">🎉</span> {t('map.breakthroughReady')}
              </p>
            )}

            <motion.div
              className={s.stampsRow}
              variants={stampsContainerVariants}
              initial="hidden"
              animate="visible"
              aria-label={t('map.stampsEarned', { earned: stampTotal, total: STAMP_TOTAL })}
            >
              {STAMPS.map((stamp, i) => {
                const earned = i < stampTotal;
                const isFinal = i === STAMP_TOTAL - 1;
                const highlightFinal = isFinal && !earned;
                return (
                  <motion.div
                    key={stamp.alt}
                    className={`${s.stamp} ${s[`stampSlot${i + 1}`]}`}
                    variants={earned ? stampVariants : undefined}
                  >
                    {earned ? (
                      <img src={stamp.src} alt={stamp.alt} className={s.stampImg} />
                    ) : (
                      <div
                        className={`${s.stampEmpty} ${highlightFinal ? s.stampEmptyFinal : ''}`}
                        aria-label={`${stamp.alt} not yet earned`}
                      />
                    )}
                  </motion.div>
                );
              })}
            </motion.div>

            {data?.sweep && (
              <section className={s.sweepCard}>
                <SweepRow
                  progress={data.sweep.progress_count}
                  target={data.sweep.target_count}
                  done={!!data.sweep.completed_at}
                  t={t}
                />
              </section>
            )}

            {(data?.core || data?.stretch) && (
              <section className={s.todayCard}>
                <p className={s.todayTitle}>{t('map.today')}</p>

                {data?.core && (
                  <TodayRow
                    label={data.core.display_name}
                    progress={data.core.progress_count}
                    target={data.core.target_count}
                    done={!!data.core.completed_at}
                    xpHint="75 XP"
                    t={t}
                  />
                )}

                {data?.stretch && (
                  data.stretch.stretch_revealed ? (
                    <TodayRow
                      label={data.stretch.display_name}
                      progress={data.stretch.progress_count}
                      target={data.stretch.target_count}
                      done={!!data.stretch.completed_at}
                      xpHint="75 XP"
                      bonus
                      t={t}
                    />
                  ) : (
                    <div className={`${s.todayRowWrap} ${s.todayRowWrapBonus}`}>
                      <span className={s.bonusPill}>{t('map.bonus')}</span>
                      <button
                        type="button"
                        className={s.revealRow}
                        onClick={() => reveal.mutate()}
                        disabled={reveal.isPending}
                      >
                        <span className={s.todayLabel}>
                          {reveal.isPending ? t('map.revealing') : t('map.revealBonus')}
                        </span>
                        <span className={s.xpReward}>+75 XP</span>
                      </button>
                    </div>
                  )
                )}
              </section>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
